import { noteAnswer } from "@/lib/api/maintenanceHold";
import type {
  AddLineRequest,
  Context,
  Category,
  ChoiceGroup,
  MenuItem,
  Order,
  OpenOrderRequest,
  Problem,
  Receipt,
  TenderRequest,
  UpdateLineRequest,
} from "@/lib/api/types";

/**
 * Typed client for the POS edge contract (docs/contracts/pos/pos-orchestrator-api.yaml).
 * Same-origin calls to /pos/v1/* are proxied to pos-orchestrator by next.config rewrites;
 * the BFF injects trusted headers. The browser only carries the session cookie + Idempotency-Key.
 */

// Matches the frozen contract server url (docs/contracts/pos/pos-orchestrator-api.yaml → servers: /pos).
export const POS_BASE = "/pos";

export class PosApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: Problem,
  ) {
    super(problem.message);
    this.name = "PosApiError";
  }
}

function idempotencyKey(): string {
  // Stable per money-moving intent; a retry with the same key never double-charges.
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

async function request<T>(path: string, init?: RequestInit & { idempotent?: boolean }): Promise<T> {
  const headers = new Headers(init?.headers);
  const language = acceptLanguage();
  if (language) headers.set("Accept-Language", language);
  if (init?.body) headers.set("Content-Type", "application/json");
  if (init?.idempotent) headers.set("Idempotency-Key", idempotencyKey());

  const res = await fetch(`${POS_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    noteAnswer(res, problem);
    sendToSignInIfSessionIsGone(res.status, problem);
    throw new PosApiError(res.status, problem);
  }
  noteAnswer(res, undefined);
  return (await res.json()) as T;
}

/**
 * A session that died mid-shift sends the cashier to sign in, not to a dead screen.
 *
 * <p>The middleware catches an expired token on navigation, but a till stays on one screen for a
 * whole shift and the token can lapse between actions. What the cashier saw then was every button
 * failing with "Authenticated session is missing required claim: locationId" — the shape of an
 * expired session, described in the vocabulary of a JWT.
 *
 * <p>Deliberately narrow: only a real authentication status. A missing `locationId` claim used to
 * be swept in here too, on the theory that it was "an expired session wearing a 400" — but a token
 * missing that claim is not expired, so the route gate's own "already signed in" shortcut for
 * `/counter` (isLive() is true) sent the browser straight back to "/", which asked `/pos/context`
 * the same question and got the same refusal: a redirect loop the cashier saw as a spinner that
 * never resolved. That case carries no fix a bounce can deliver — the token itself would need
 * reissuing with a location — so it is left for the caller (`usePosSession`) to recognise and
 * explain, rather than acted on here. A business error must never bounce someone out of a bill
 * they are ringing.
 */
function sendToSignInIfSessionIsGone(status: number, problem: Problem): void {
  if (typeof window === "undefined") return;
  if (status !== 401 && status !== 403) return;
  const here = window.location.pathname + window.location.search;
  // Signing in and pairing a fresh till both happen before there is a session to lose.
  if (window.location.pathname === "/counter" || window.location.pathname === "/pair") return;
  window.location.assign(`/counter?next=${encodeURIComponent(here)}`);
}

/**
 * True when a `Problem` is the edge's answer to a token with no `locationId` claim
 * (`INVALID_POS_CONTEXT`, 422) — a staff member with no active row in
 * `user.pos_staff_location_assignment`. Retrying never fixes it; only a fresh sign-in with a
 * counter can. Shared with `usePosSession`, which is the one place this is shown to a cashier.
 */
export function isMissingLocationClaim(problem: Problem): boolean {
  return typeof problem.message === "string" && problem.message.includes("missing required claim");
}

/**
 * What is unset on a counter that has not finished being set up, or null when this is not that.
 *
 * Matched on the CODE, never on the 5xx status. `POS_CONTEXT_UNAVAILABLE` and a genuine outage both
 * arrive as a 503, and they need opposite screens: a missing `pos.locale` row is not going to fix
 * itself however many times a cashier presses Try again, and a pos-orchestrator that is down is not
 * a settings problem. Keying on the status would collapse them back into one, which is the state
 * this function exists to end.
 *
 * `reason` is the edge's own sentence, shown to the operator verbatim. `missing` is the stable key
 * of the unset fact (`pos.locale`, `location.pack`) — absent when the edge could not name one fact,
 * which is the honest answer for an owner outage wearing the same code. `role` is the signed-in
 * staff role, sent here because the call that normally carries it is the call that just failed.
 */
export function posContextGap(
  problem: Problem,
): { reason: string; missing?: string; role?: string } | null {
  if (problem.code !== "POS_CONTEXT_UNAVAILABLE") return null;
  const details = problem.details ?? {};
  const text = (value: unknown) => (typeof value === "string" && value.trim() !== "" ? value : undefined);
  return {
    // The generic envelope message is the last resort: it says "try again", which is exactly the
    // advice this screen exists to stop giving. `reason` is what somebody can act on.
    reason: text(details.reason) ?? problem.message,
    missing: text(details.missing),
    role: text(details.role),
  };
}

/**
 * The language the till is showing, as the header the edge renders its words in.
 *
 * `CounterIntlProvider` keeps `<html lang>` on the resolved counter language (staff, then device,
 * then business), so this reads the one place that already knows. Absent on the server render, and
 * the edge then answers in en-IN.
 */
export function acceptLanguage(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const lang = document.documentElement.lang;
  return lang && lang.trim() !== "" ? lang : undefined;
}

/**
 * The sentence a till shows for a refused intent.
 *
 * The edge answers a catalogued refusal with `message` and `recovery` in the language sent in
 * `Accept-Language`, so those are shown as they arrive; `problem.code` stays the wire contract and
 * is never written back. The only words kept here are for a problem that arrived with no message.
 */
export function friendlyProblemMessage(problem: Problem): string {
  if (problem.code === "INSUFFICIENT_STOCK" && problem.message === problem.code) {
    // Only when the edge sent the bare code with no words. `available` rides the owner's 409 as a
    // structured extra; a code with no such fact still gets a plain refusal rather than a blank notice.
    const available = problem.details?.available;
    return typeof available === "number"
      ? `Not enough in stock at this counter — ${available} left.`
      : "Not enough in stock at this counter.";
  }
  const message = typeof problem.message === "string" ? problem.message.trim() : "";
  if (message === "") return problem.recovery?.trim() || GENERIC_PROBLEM;
  return problem.recovery ? `${message} ${problem.recovery}` : message;
}

/** For a problem that carries no message at all. */
const GENERIC_PROBLEM = "Something went wrong. Try again.";

const json = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });

export const posClient = {
  getContext: () => request<Context>("/context"),

  getMenu: (category?: string) =>
    request<{ categories?: Category[]; items: MenuItem[] }>(
      `/menu${category ? `?category=${encodeURIComponent(category)}` : ""}`,
    ),

  /** The questions asked when an item is sold; an item with none answers an empty list. */
  getChoices: (itemId: string) =>
    request<{ groups: ChoiceGroup[] }>(`/menu/${encodeURIComponent(itemId)}/choices`),

  openOrder: (body: OpenOrderRequest) => request<Order>("/orders", json(body)),

  getOrder: (orderId: string) => request<Order>(`/orders/${orderId}`),

  addLine: (orderId: string, body: AddLineRequest) =>
    request<Order>(`/orders/${orderId}/lines`, json(body)),

  updateLine: (orderId: string, lineId: string, body: UpdateLineRequest) =>
    request<Order>(`/orders/${orderId}/lines/${lineId}`, { method: "PATCH", body: JSON.stringify(body) }),

  voidLine: (orderId: string, lineId: string, reason?: string) =>
    request<Order>(`/orders/${orderId}/lines/${lineId}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`, {
      method: "DELETE",
    }),

  applyPromo: (orderId: string, code: string) =>
    request<Order>(`/orders/${orderId}/promo`, json({ code })),

  removePromo: (orderId: string) => request<Order>(`/orders/${orderId}/promo`, { method: "DELETE" }),

  addTender: (orderId: string, body: TenderRequest) =>
    request<Order>(`/orders/${orderId}/tenders`, json(body)),

  removeTender: (orderId: string, tenderId: string) =>
    request<Order>(`/orders/${orderId}/tenders/${tenderId}`, { method: "DELETE" }),

  confirm: (orderId: string) =>
    request<Receipt>(`/orders/${orderId}/confirm`, { method: "POST", idempotent: true }),

  streamUrl: (scope: string) => `${POS_BASE}/stream?scope=${encodeURIComponent(scope)}`,
};

export type PosClient = typeof posClient;
